import { AccountInfo, AccountRule } from '../context/account_context'
import { CoinInfo } from '../context/coin_context'
import { ListReturn } from '../utils/maks'
import { BranchApiData, parseBranch } from './branch'
import { EmployeeApiData, parseEmployee } from './employee'
import { get, post, put } from './generic'

export interface RuleApiData {
    id_permissao: number
    nome: string
    grupo: string
}

export interface AccountApiData {
    id_usuario: number
    nome: string
    username: string
    usuario_permissao: {
        id_permissao: number
        id_usuario: number
    }[]
    flag_master: number
    flag_ativo: number
    flag_login_face: number
    id_filial: number | null
    path_face: string | null
    permissions?: string[]
    filial: BranchApiData | null
    flag_ignorar_captcha: number
    prefixo_preferido: string | null
    host_impressora_local: string | null
    flag_2fa?: number
    funcionarios?: EmployeeApiData[]
}

export interface CreateAccountInterface {
    nome: string
    username: string
    senha?: string
    flag_ativo: number
    id_permissions: number[]
    flag_login_face: number
    path_face: string | null
    id_filial_master?: number | null
    flag_ignorar_captcha: number
    prefixo_preferido: string | null
    host_impressora_local: string | null
    flag_2fa?: number
}

export interface UpdateAccountInterface
    extends Partial<CreateAccountInterface> {
    id_usuario: number
    reset_2fa?: boolean
}

export interface TwoFactorSetup {
    secret_2fa: string | null
    otpauth_url: string | null
}

export interface ListAccountsInterface {
    page: number
    rows: number
    search?: string
    id_filial?: number[]
}

export function parseRule(data: RuleApiData): AccountRule {
    return {
        id: data.id_permissao,
        name: data.nome,
        group: data.grupo,
    }
}

export function parseAccount(data: AccountApiData, rules: AccountRule[], coins: CoinInfo[]): AccountInfo {
    return {
        id: data.id_usuario,
        name: data.nome,
        username: data.username,
        password: '',
        cpassword: '',
        rules: data.usuario_permissao.map(
            (p) => rules.find((r) => r.id === p.id_permissao) as AccountRule
        ),
        isMaster: Boolean(data.flag_master),
        isActive: Boolean(data.flag_ativo),
        loginWithImage: Boolean(data.flag_login_face),
        image: data.path_face
            ? {
                  content: data.path_face,
                  file: new File([], data.path_face),
                  isBase64: false,
              }
            : null,
        branch: data.filial ? parseBranch(data.filial, coins) : null,
        permissions: data.permissions,
        ignoreCaptcha: Boolean(data.flag_ignorar_captcha),
        preferedNotePrefix: data.prefixo_preferido,
        localPrinterHost: data.host_impressora_local,
        twoFactorEnabled: Boolean(data.flag_2fa),
        employees: data.funcionarios?.map((f) => parseEmployee(f, coins))
    }
}

export class UsernameAlreadyInUseError extends Error {}

export async function listAccountRules(): Promise<AccountRule[]> {
    const res = await get('permissions')
    return res.data.data.map((data: RuleApiData) => parseRule(data))
}

export async function createAccount(
    data: CreateAccountInterface
): Promise<{ id_usuario: number; twoFactor: TwoFactorSetup | null }> {
    try {
        const res = await post('user', data, {
            useRecaptcha: true,
        })
        return {
            id_usuario: res.data.data.user.id_usuario,
            twoFactor: res.data.data.secret_2fa
                ? {
                      secret_2fa: res.data.data.secret_2fa,
                      otpauth_url: res.data.data.otpauth_url,
                  }
                : null,
        }
    } catch (error: any) {
        if (
            error?.status === 409 &&
            error?.message?.includes('User with username already exists')
        ) {
            throw new UsernameAlreadyInUseError()
        }
        throw error
    }
}

export async function updateAccount(
    data: UpdateAccountInterface
): Promise<TwoFactorSetup | null> {
    try {
        const res = await put('user', data, {
            useRecaptcha: true,
        })
        return res.data.data?.secret_2fa
            ? {
                  secret_2fa: res.data.data.secret_2fa,
                  otpauth_url: res.data.data.otpauth_url,
              }
            : null
    } catch (error: any) {
        if (
            error?.status === 409 &&
            error?.message.includes('User with username already exists')
        ) {
            throw new UsernameAlreadyInUseError()
        }
        throw error
    }
}

export async function listAccounts(
    data: ListAccountsInterface,
    rules: AccountRule[],
    coins: CoinInfo[]
): Promise<ListReturn<AccountInfo>> {
    const res = await post('users', data)
    return {
        data: res.data.data.data.map((data: AccountApiData) =>
            parseAccount(data, rules, coins)
        ),
        count: res.data.data.count,
    }
}

export async function getAccountById(
    id: number,
    rules: AccountRule[],
    coins: CoinInfo[]
): Promise<AccountInfo> {
    const res = await get(`user/specific/${id}`)
    return parseAccount(res.data.data.user, rules, coins)
}

export async function getLoggedUser(
    rules: AccountRule[],
    coins: CoinInfo[]
): Promise<AccountInfo> {
    const res = await get('user/logged')
    return parseAccount(res.data.data.user, rules, coins)
}
